/**
 * Edge STT seam — WO-24.
 *
 * INVARIANT (mobile CLAUDE.md §5): audio is transcribed ON DEVICE (at the edge)
 * and only TEXT is sent to the backend. This module is the ONLY transcription
 * boundary in the app. NEVER use the default web-speech-api — it is unavailable
 * in Expo and violates the edge-only constraint.
 *
 * ── Gate Q5 (RESOLVED) ───────────────────────────────────────────────────────
 * Provider chosen: `expo-speech-recognition` (community Expo config-plugin
 * library wrapping the native on-device recognizers — SFSpeechRecognizer on iOS
 * and android.speech.SpeechRecognizer on Android). It is NOT web-speech-api: the
 * native module performs recognition on-device where supported and never touches
 * the BEAM backend. Only the resulting transcript text leaves the device.
 *
 * PER-PLATFORM REALITY (documented, not assumed):
 *   - iOS: true on-device recognition is available on most modern devices. We
 *     request it via `requiresOnDeviceRecognition` when the device reports
 *     support (`supportsOnDeviceRecognition()`). When unsupported we still run
 *     recognition (Apple may use its server path) — but the library still keeps
 *     audio off OUR backend either way; only text reaches BEAM.
 *   - Android: on-device recognition depends on the recognition service
 *     (`com.google.android.as`) and an installed offline locale model. Many
 *     devices ship only the NETWORK recognizer (Google) — in that case Android
 *     sends audio to Google's STT, not to BEAM. We prefer on-device when the
 *     device supports it; otherwise we fall back to the platform default
 *     recognizer. Either way BEAM only ever receives text. See README note in
 *     the WO-24 report ("Android may need a network engine").
 *
 * FLOW (live): the library is event-driven (start → speak → stop → final
 * `result` event). There is no synchronous "transcribe(file)" primitive that is
 * reliable across platforms, so the real provider exposes `transcribeLive()`
 * which drives a start/stop session and resolves with the final transcript.
 * The file-based `transcribe(audioUri)` method is also implemented (it feeds the
 * recorded URI to the recognizer via `audioSource`) and remains in the seam for
 * callers that already have a recording, but the voice screen uses the live flow
 * because that is what the platform recognizers support most reliably.
 *
 * FALLBACK: on permission denial, unsupported platform, or recognizer error we
 * throw a typed `SttError`. The voice screen catches it and shows the editable
 * manual-transcript TextInput. We NEVER fall back to web-speech-api.
 * ─────────────────────────────────────────────────────────────────────────────
 */

// expo-speech-recognition 3.x removed the top-level addSpeechRecognitionListener /
// supportsOnDeviceRecognition functions; both are now methods on the module object.
import { ExpoSpeechRecognitionModule } from 'expo-speech-recognition';
import type {
  ExpoSpeechRecognitionErrorCode,
  ExpoSpeechRecognitionOptions,
} from 'expo-speech-recognition';

// ---------------------------------------------------------------------------
// Seam interface
// ---------------------------------------------------------------------------

/**
 * Handle returned by `transcribeLive()`. The caller starts a session, then:
 *   - awaits `result` to get the final transcript (resolves when recognition
 *     ends), or
 *   - calls `stop()` to ask the recognizer to finalise early (the user tapped
 *     "Stop"), or
 *   - calls `abort()` to cancel with no result (the user discarded).
 */
export interface LiveTranscription {
  /** Resolves with the final transcript text, or rejects with an SttError. */
  readonly result: Promise<string>;
  /** Ask the recognizer to stop and emit its final result. */
  stop(): void;
  /** Cancel immediately; `result` rejects with an `aborted` SttError. */
  abort(): void;
}

/**
 * Contract every STT provider must implement.
 *
 * NEVER implement using web-speech-api — the API is unavailable in Expo.
 */
/** Options for a live transcription session. */
export interface TranscribeLiveOptions {
  /**
   * Called with the growing interim transcript as the recognizer emits non-final
   * `result` events (`isFinal: false`). When supplied, the session requests
   * `interimResults: true`. Omit for the original behavior (final transcript only).
   */
  onPartial?: (text: string) => void;
}

export interface Transcriber {
  /**
   * Live recognition: start a session and return a handle. The screen renders a
   * "speak now / Stop" UI and resolves the transcript when `stop()` is called or
   * the recognizer auto-finalises. Pass `onPartial` to also stream interim text
   * (e.g. to show live-typed words before the user stops speaking).
   */
  transcribeLive(opts?: TranscribeLiveOptions): Promise<LiveTranscription>;
  /**
   * File-based recognition: transcribe an already-recorded local audio URI
   * (e.g. from expo-av). Resolves to the transcript text or rejects with an
   * SttError. Kept in the seam for callers that already hold a recording.
   */
  transcribe(audioUri: string): Promise<string>;
}

// ---------------------------------------------------------------------------
// Typed error
// ---------------------------------------------------------------------------

export type SttErrorCode =
  | 'stt_provider_not_configured' // legacy: no provider wired (stub only)
  | 'stt_permission_denied'       // OS mic/speech-recognition permission denied
  | 'stt_no_speech_detected'      // recording contained no recognisable speech
  | 'stt_unsupported'             // recognizer unavailable on this device
  | 'stt_aborted'                 // session cancelled before a result
  | 'stt_provider_error';         // provider-internal failure

export class SttError extends Error {
  constructor(
    public readonly code: SttErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'SttError';
  }
}

/** Map an expo-speech-recognition native error code to our typed SttError. */
function mapNativeError(code: ExpoSpeechRecognitionErrorCode, message: string): SttError {
  switch (code) {
    case 'not-allowed':
    case 'service-not-allowed':
      return new SttError('stt_permission_denied', message || 'Speech recognition permission denied.');
    case 'no-speech':
    case 'speech-timeout':
      return new SttError('stt_no_speech_detected', message || 'No speech detected.');
    case 'language-not-supported':
      return new SttError('stt_unsupported', message || 'Recognition language not supported on this device.');
    case 'aborted':
      return new SttError('stt_aborted', message || 'Speech recognition was cancelled.');
    default:
      return new SttError('stt_provider_error', message || `Speech recognition error (${code}).`);
  }
}

// ---------------------------------------------------------------------------
// Permission + capability helpers
// ---------------------------------------------------------------------------

/**
 * Ensure mic + speech-recognition permission. Throws `stt_permission_denied`
 * (caught by the screen → manual entry) when the user declines.
 */
async function ensurePermission(): Promise<void> {
  const current = await ExpoSpeechRecognitionModule.getPermissionsAsync();
  if (current.granted) return;
  if (!current.canAskAgain) {
    throw new SttError(
      'stt_permission_denied',
      'Speech recognition permission is blocked. Enable it in Settings, or type your transcript below.',
    );
  }
  const result = await ExpoSpeechRecognitionModule.requestPermissionsAsync();
  if (!result.granted) {
    throw new SttError(
      'stt_permission_denied',
      'Speech recognition permission was denied. Type your transcript below.',
    );
  }
}

/**
 * Build the recognizer options. We request ON-DEVICE recognition when the
 * device supports it (keeps audio off any network). When it does not, the
 * recognizer uses the platform default (which on Android may be the network
 * engine) — BEAM still only receives text.
 */
function buildOptions(extra?: Partial<ExpoSpeechRecognitionOptions>): ExpoSpeechRecognitionOptions {
  const onDevice = safeSupportsOnDevice();
  return {
    lang: 'en-US',
    interimResults: false,
    continuous: false,
    addsPunctuation: true,
    requiresOnDeviceRecognition: onDevice,
    ...extra,
  };
}

function safeSupportsOnDevice(): boolean {
  try {
    return ExpoSpeechRecognitionModule.supportsOnDeviceRecognition();
  } catch {
    return false;
  }
}

/** True if the recognizer can run at all on this device. */
function recognitionAvailable(): boolean {
  try {
    return ExpoSpeechRecognitionModule.isRecognitionAvailable();
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Shared session driver (used by both live and file-based recognition)
// ---------------------------------------------------------------------------

/**
 * Start a recognition session and wire up listeners. Returns a `LiveTranscription`
 * whose `result` resolves with the best final transcript and rejects with a
 * typed `SttError`. Listeners are always torn down on settle.
 */
function startSession(options: ExpoSpeechRecognitionOptions, onPartial?: (text: string) => void): LiveTranscription {
  let settled = false;
  let latestTranscript = '';
  let pendingError: SttError | null = null;

  // Subscriptions, removed on settle.
  const subs: Array<{ remove: () => void }> = [];

  let resolveResult!: (text: string) => void;
  let rejectResult!: (err: SttError) => void;
  const resultPromise = new Promise<string>((resolve, reject) => {
    resolveResult = resolve;
    rejectResult = reject;
  });

  function cleanup(): void {
    for (const s of subs) {
      try {
        s.remove();
      } catch {
        /* ignore */
      }
    }
    subs.length = 0;
  }

  function settleResolve(text: string): void {
    if (settled) return;
    settled = true;
    cleanup();
    resolveResult(text);
  }

  function settleReject(err: SttError): void {
    if (settled) return;
    settled = true;
    cleanup();
    rejectResult(err);
  }

  subs.push(
    ExpoSpeechRecognitionModule.addListener('result', (ev) => {
      // Capture the strongest transcript we have seen so far. With
      // interimResults:false the meaningful event is the final one, but we keep
      // the latest defensively in case the platform only emits on `end`.
      const best = ev.results?.[0]?.transcript ?? '';
      if (best.length > 0) latestTranscript = best;
      if (ev.isFinal) {
        const text = latestTranscript.trim();
        if (text.length === 0) {
          settleReject(new SttError('stt_no_speech_detected', 'No speech was recognised.'));
        } else {
          settleResolve(text);
        }
      } else if (onPartial && best.length > 0) {
        // Interim result — stream it to the caller. The final transcript still
        // settles the promise above; this never resolves/rejects the session.
        onPartial(best);
      }
    }),
  );

  subs.push(
    ExpoSpeechRecognitionModule.addListener('error', (ev) => {
      pendingError = mapNativeError(ev.error, ev.message);
      // Some errors arrive without a following `end`; settle eagerly.
      settleReject(pendingError);
    }),
  );

  subs.push(
    ExpoSpeechRecognitionModule.addListener('end', () => {
      // `end` fires after stop/auto-finalise. If we already have a final result
      // we will have settled; otherwise resolve with whatever we captured, or
      // surface a pending error / no-speech.
      if (settled) return;
      if (pendingError) {
        settleReject(pendingError);
        return;
      }
      const text = latestTranscript.trim();
      if (text.length > 0) {
        settleResolve(text);
      } else {
        settleReject(new SttError('stt_no_speech_detected', 'No speech was recognised.'));
      }
    }),
  );

  try {
    ExpoSpeechRecognitionModule.start(options);
  } catch (err) {
    settleReject(
      new SttError(
        'stt_provider_error',
        err instanceof Error ? err.message : 'Failed to start speech recognition.',
      ),
    );
  }

  return {
    result: resultPromise,
    stop() {
      try {
        ExpoSpeechRecognitionModule.stop();
      } catch {
        /* the `end`/`error` listener will settle */
      }
    },
    abort() {
      try {
        ExpoSpeechRecognitionModule.abort();
      } catch {
        /* ignore */
      }
      settleReject(new SttError('stt_aborted', 'Speech recognition cancelled.'));
    },
  };
}

// ---------------------------------------------------------------------------
// Real provider — expo-speech-recognition (Gate Q5 resolved)
// ---------------------------------------------------------------------------

export const expoSpeechRecognitionTranscriber: Transcriber = {
  async transcribeLive(opts?: TranscribeLiveOptions): Promise<LiveTranscription> {
    if (!recognitionAvailable()) {
      throw new SttError(
        'stt_unsupported',
        'On-device speech recognition is unavailable on this device. Type your transcript below.',
      );
    }
    await ensurePermission();
    return startSession(buildOptions({ interimResults: !!opts?.onPartial }), opts?.onPartial);
  },

  async transcribe(audioUri: string): Promise<string> {
    if (!recognitionAvailable()) {
      throw new SttError(
        'stt_unsupported',
        'On-device speech recognition is unavailable on this device.',
      );
    }
    await ensurePermission();
    const session = startSession(
      buildOptions({
        audioSource: { uri: audioUri },
      }),
    );
    return session.result;
  },
};

// ---------------------------------------------------------------------------
// Stub transcriber (kept for tests / explicit "no provider" simulation)
// ---------------------------------------------------------------------------

/**
 * Placeholder transcriber retained for tests and for environments where the
 * native module is unavailable (e.g. Expo Go without the dev client). Throws an
 * `SttError` so callers fall through to the manual-entry path.
 *
 * IMPORTANT: never calls web-speech-api and never sends audio to the backend.
 */
export const stubTranscriber: Transcriber = {
  async transcribeLive(_opts?: TranscribeLiveOptions): Promise<LiveTranscription> {
    throw new SttError(
      'stt_provider_not_configured',
      'Edge STT provider not active. Type your transcript in the text field below.',
    );
  },
  async transcribe(_audioUri: string): Promise<string> {
    throw new SttError(
      'stt_provider_not_configured',
      'Edge STT provider not active. Type your transcript in the text field below.',
    );
  },
};

// ---------------------------------------------------------------------------
// Active transcriber
// ---------------------------------------------------------------------------

/**
 * The transcriber the voice screen uses.
 *
 * GATE Q5 RESOLVED: points to the real `expoSpeechRecognitionTranscriber`
 * (on-device where supported; text-only to BEAM; never web-speech-api).
 */
export const activeTranscriber: Transcriber = expoSpeechRecognitionTranscriber;

// ---------------------------------------------------------------------------
// Manual-transcript path (always available fallback)
// ---------------------------------------------------------------------------

/**
 * Helper the voice screen uses when the user types their own transcript instead
 * of (or after) using STT. Returns the text trimmed. This is the permission-
 * denied / unsupported / error fallback path.
 */
export function manualTranscript(text: string): string {
  return text.trim();
}
